import { useRef, useState } from "react";
import { Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, errMsg } from "@/lib/api";
import { useContactOptions } from "@/lib/lookups";
import { fmtSize, ONE_PAGE_ACCEPT, validateOnePage } from "@/lib/onePages";

const localToday = () => new Date().toLocaleDateString("en-CA");

const SenderSelect = ({ value, onChange, options, contacts }) => {
  const involvedIds = new Set(options.involved.map((c) => c.id));
  return (
    <label className="block space-y-1"><span className="text-xs c-text-2">Enviado por</span>
      <select data-testid="onepage-sender-select" className="c-input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Sin especificar</option>
        {options.involved.length > 0 && <optgroup label="Involucrados">{options.involved.map((c) => <option key={c.id} value={`c:${c.id}`}>{c.name} · {c.role}</option>)}</optgroup>}
        <optgroup label="Equipo">{options.users.map((u) => <option key={u.id} value={`u:${u.id}`}>{u.name}</option>)}</optgroup>
        <optgroup label="Otros contactos">{contacts.filter((c) => !involvedIds.has(c.id)).map((c) => <option key={c.id} value={`c:${c.id}`}>{c.name}</option>)}</optgroup>
      </select>
    </label>
  );
};

export const OnePageUpload = ({ oppId, options, open, onOpenChange, onDone }) => {
  const contacts = useContactOptions();
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [sender, setSender] = useState("");
  const [date, setDate] = useState(localToday());
  const [note, setNote] = useState("");
  const [progress, setProgress] = useState(null);

  const pick = (f) => {
    if (!f) return;
    const err = validateOnePage(f);
    if (err) { toast.error(err); return; }
    setFile(f);
  };
  const submit = async () => {
    const fd = new FormData();
    fd.append("file", file);
    const [kind, id] = sender.split(":");
    if (kind === "c") fd.append("sender_contact_id", id);
    if (kind === "u") fd.append("sender_user_id", id);
    fd.append("received_date", date);
    if (note.trim()) fd.append("note", note.trim());
    setProgress(0);
    try {
      const { data } = await api.post(`/opportunities/${oppId}/one-pages`, fd, { onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)) });
      toast.success("One page guardado");
      setFile(null); setSender(""); setNote(""); setDate(localToday());
      onDone(data);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setProgress(null); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" data-testid="onepage-upload-sheet" className="max-h-[92vh] overflow-y-auto rounded-t-2xl pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <SheetHeader><SheetTitle>Subir one page</SheetTitle><SheetDescription>PDF, PPT/PPTX, KEY, DOC/DOCX o imagen. Máximo 25 MB.</SheetDescription></SheetHeader>
        <div className="space-y-3 mt-4">
          <input ref={input} data-testid="onepage-file-input" type="file" accept={ONE_PAGE_ACCEPT} className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
          <button data-testid="onepage-pick-btn" onClick={() => input.current?.click()} className="w-full c-card p-4 rounded-xl text-sm flex items-center gap-2 text-left">
            <Paperclip className="w-4 h-4 c-accent shrink-0" />
            <span className="flex-1 truncate">{file ? `${file.name} · ${fmtSize(file.size)}` : "Elegir archivo"}</span>
          </button>
          <SenderSelect value={sender} onChange={setSender} options={options} contacts={contacts} />
          <label className="block space-y-1"><span className="text-xs c-text-2">Fecha de recibido</span>
            <input data-testid="onepage-date-input" type="date" className="c-input" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="block space-y-1"><span className="text-xs c-text-2">Nota (opcional)</span>
            <input data-testid="onepage-note-input" maxLength={300} className="c-input" placeholder="Ej. versión actualizada con precios 2026" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <button data-testid="onepage-submit-btn" disabled={!file || progress !== null || !date} onClick={submit} className="c-btn-primary w-full py-3 flex items-center justify-center gap-2">
            {progress !== null && <Loader2 className="w-4 h-4 animate-spin" />} {progress !== null ? `Subiendo… ${progress}%` : "Guardar one page"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
